/**
 * MoveToListModal — center modal listing eligible destinations. Bottom
 * row is "+ Add new list" which expands an inline input to create and
 * then move in one gesture.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { Ionicons } from '@expo/vector-icons';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { TaskList } from '@/types/task-lists';
import { TASK_LIST_NAME_MAX } from '@/types/task-lists';

type MoveToListModalProps = {
  visible: boolean;
  lists: TaskList[];
  currentListId: string | null;
  atCap: boolean;
  onSelectList: (listId: string) => void;
  onAddList: (name: string) => Promise<string>; // returns new list id
  onDismiss: () => void;
};

export function MoveToListModal({
  visible,
  lists,
  currentListId,
  atCap,
  onSelectList,
  onAddList,
  onDismiss,
}: MoveToListModalProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const scale = useSharedValue(0.95);
  const opacity = useSharedValue(0);
  const [addingNew, setAddingNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      scale.value = withTiming(1, { duration: 150 });
      opacity.value = withTiming(1, { duration: 150 });
      setAddingNew(false);
      setNewName('');
      setError(null);
    } else {
      scale.value = withTiming(0.95, { duration: 150 });
      opacity.value = withTiming(0, { duration: 150 });
    }
  }, [visible, scale, opacity]);

  const cardStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
    opacity: opacity.value,
  }));

  const backdropStyle = useAnimatedStyle(() => ({ opacity: opacity.value * 0.5 }));

  const handleAddAndMove = useCallback(async () => {
    const trimmed = newName.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const newId = await onAddList(trimmed);
      onSelectList(newId);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }, [newName, submitting, onAddList, onSelectList]);

  return (
    <Modal visible={visible} transparent animationType="none" onRequestClose={onDismiss}>
      <Animated.View style={[styles.backdrop, backdropStyle]}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onDismiss} />
      </Animated.View>
      <View style={styles.centered} pointerEvents="box-none">
        <Animated.View
          style={[styles.card, { backgroundColor: palette.cardBackground }, cardStyle]}
        >
          <Text style={[styles.title, { color: palette.text }]}>Move to list</Text>
          {lists.map(list => {
            const isCurrent = list.id === currentListId;
            return (
              <TouchableOpacity
                key={list.id}
                onPress={() => (isCurrent ? onDismiss() : onSelectList(list.id))}
                activeOpacity={0.7}
                style={styles.row}
              >
                <Text style={[styles.rowLabel, { color: palette.text }]} numberOfLines={1}>
                  {list.name}
                </Text>
                {isCurrent && (
                  <Ionicons name="checkmark" size={18} color={palette.tint} />
                )}
              </TouchableOpacity>
            );
          })}
          <View style={[styles.separator, { backgroundColor: palette.borderMuted }]} />
          {addingNew ? (
            <View style={styles.addRow}>
              <TextInput
                value={newName}
                onChangeText={setNewName}
                autoFocus
                maxLength={TASK_LIST_NAME_MAX}
                placeholder="New list name"
                placeholderTextColor={palette.textMuted}
                onSubmitEditing={handleAddAndMove}
                returnKeyType="done"
                style={[styles.input, { color: palette.text, borderColor: palette.borderMuted }]}
              />
              <TouchableOpacity
                onPress={handleAddAndMove}
                disabled={!newName.trim() || submitting}
                style={[
                  styles.goButton,
                  { backgroundColor: palette.tint, opacity: !newName.trim() || submitting ? 0.5 : 1 },
                ]}
              >
                <Ionicons name="arrow-forward" size={18} color="#fff" />
              </TouchableOpacity>
            </View>
          ) : (
            <TouchableOpacity
              onPress={() => setAddingNew(true)}
              disabled={atCap}
              activeOpacity={0.7}
              style={[styles.row, atCap && { opacity: 0.5 }]}
            >
              <Ionicons name="add" size={18} color={palette.tint} />
              <Text style={[styles.rowLabel, { color: palette.tint }]}>Move to new list</Text>
            </TouchableOpacity>
          )}
          {error && <Text style={[styles.error, { color: palette.danger }]}>{error}</Text>}
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: { ...StyleSheet.absoluteFillObject, backgroundColor: '#000' },
  centered: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  card: {
    width: 320,
    padding: Spacing.lg,
    borderRadius: BorderRadius.lg,
    gap: Spacing.xs,
  },
  title: {
    fontSize: 17,
    fontWeight: '600',
    marginBottom: Spacing.sm,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    paddingHorizontal: Spacing.sm,
    paddingVertical: Spacing.md,
  },
  rowLabel: { fontSize: 15, flex: 1 },
  separator: { height: 1, marginVertical: Spacing.xs },
  addRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    paddingVertical: Spacing.sm,
  },
  input: {
    flex: 1,
    borderWidth: 1,
    borderRadius: BorderRadius.md,
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm,
    fontSize: 15,
  },
  goButton: {
    padding: Spacing.sm,
    borderRadius: BorderRadius.md,
  },
  error: { fontSize: 13, paddingHorizontal: Spacing.sm },
});
